/**
 * @fileoverview MyTour.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useEffect, useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom';
import { IMG } from '../assets/Assets';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import DynamicImage from '../components/ui/DynamicImage'
import SEO from '../components/ui/SEO'
import { pdf } from '@react-pdf/renderer';
import ItineraryPDF from '../components/pdf/ItineraryPDF';
import pdfLogo from '../assets/Media/KITH-Logo.png';

function MyTour() {
    const { t } = useTranslation();
    const { tourId } = useParams();
    
    const [loading, setLoading] = useState(true);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [tourData, setTourData] = useState(null);

    const [showGallery, setShowGallery] = useState(false);
    const [activePhotoIndex, setActivePhotoIndex] = useState(0);
    const [showItinerary, setShowItinerary] = useState(false);
    const [activeProfilePhoto, setActiveProfilePhoto] = useState(null);

    const [showAccountDetails, setShowAccountDetails] = useState(false);
    const [showPaymentHistory, setShowPaymentHistory] = useState(false);
    const [editingTraveler, setEditingTraveler] = useState(null);

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const [editingFlights, setEditingFlights] = useState(false);
    const [flightForm, setFlightForm] = useState({
        arrival: { date: '', time: '', flight: '' },
        departure: { date: '', time: '', flight: '' }
    });

    useEffect(() => {
        if (tourData) {
            setFlightForm({
                arrival: { 
                    date: tourData.arrival?.date || '',
                    time: tourData.arrival?.time || '', 
                    flight: tourData.arrival?.flight || '' 
                },
                departure: { 
                    date: tourData.departure?.date || '',
                    time: tourData.departure?.time || '', 
                    flight: tourData.departure?.flight || '' 
                }
            });
        }
    }, [tourData]);

    const handleFlightSave = async () => {
        try {
            const payload = {
                arrival: { ...tourData.arrival, date: flightForm.arrival.date, time: flightForm.arrival.time, flight: flightForm.arrival.flight },
                departure: { ...tourData.departure, date: flightForm.departure.date, time: flightForm.departure.time, flight: flightForm.departure.flight }
            };
            const res = await apiFetch(`/bookings/track/${tourData.confirmationNumber}/flights`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
            if (res && res.success) {
                setTourData({
                    ...tourData,
                    arrival: payload.arrival,
                    departure: payload.departure
                });
                setEditingFlights(false);
            } else {
                alert('Failed to update flights');
            }
        } catch (error) {
            console.error(error);
            alert('Failed to update flights');
        }
    };

    // ── Self-contained PDF download — no external service required ──────────────
    
    
    const convertWebPtoJPEG = (url) => {
        if (!url) return null;
        let finalUrl = url;
        if (finalUrl.startsWith('/')) {
            finalUrl = 'https://engine.kithtravels.com' + finalUrl;
        }
        // Use an image proxy to avoid CORS and convert WebP to JPG for PDF renderer
        return `https://wsrv.nl/?url=${encodeURIComponent(finalUrl)}&output=jpg`;
    };

    const handleDownloadPDF = async () => {
        setIsGeneratingPDF(true);
        
        // Artificial delay for UX as requested
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        try {
            // Pre-process all experience images to JPEG for the PDF
            const processedItinerary = await Promise.all((detailedItinerary || []).map(async (d, idx) => {
                const processedExps = await Promise.all((d.experiences || []).map(async (exp) => {
                    const imgUrl = typeof exp === 'object' ? (exp.image?.h || exp.image?.l || exp.image) : null;
                    if (imgUrl) {
                        const jpegUrl = await convertWebPtoJPEG(imgUrl);
                        return { ...exp, image: jpegUrl };
                    }
                    return exp;
                }));
                
                return {
                    day: d.day || idx + 1,
                    title: d.title || d.location || `Day ${idx + 1}`,
                    subtitle: d.driveTime || d.subtitle || '',
                    description: d.description || '',
                    experiences: processedExps,
                };
            }));

            const doc = (
                <ItineraryPDF
                    logoUrl={pdfLogo}
                    tourName={tourData.tourPackage?.title || tourData.tourPackage?.name || 'Tour Itinerary'}
                    tourRef={tourData.confirmationNumber || ''}
                    clientName={tourData.customer?.fname ? tourData.customer.fname + ' ' + (tourData.customer.lname || '') : ''}
                    arrivalDate={tourData.arrival?.date || arrivalDate || ''}
                    arrivalFlight={tourData.arrival?.flight || ''}
                    arrivalTime={tourData.arrival?.time || ''}
                    departureDate={tourData.departure?.date || departureDate || ''}
                    departureFlight={tourData.departure?.flight || ''}
                    departureTime={tourData.departure?.time || ''}
                    itinerary={processedItinerary}
                    inclusions={tourData.tourPackage?.inclusions || tourData.includes || ['Accommodation', 'Transport', 'Expert Guide', 'Entrance Fees']}
                    exclusions={tourData.tourPackage?.exclusions || tourData.excludes || ['International Flights', 'Visa Fees', 'Travel Insurance', 'Personal Expenses']}
                    featuredInclusions={tourData.featuredInclusions || tourData.includes || []}
                />
            );
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            setIsGeneratingPDF(false);

            setTimeout(() => {
                a.download = `KITH_Itinerary_${tourData.confirmationNumber || 'Tour'}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }, 100);
        } catch (err) {
            console.error('PDF generation failed:', err);
            alert('Failed to generate PDF. Please try again.');
            setIsGeneratingPDF(false);
        }
    };

    const handleTravelerSave = async () => {
        if (!editingTraveler) return;
        try {
            const payload = {
                travelers: [{
                    index: editingTraveler.index,
                    passportName: editingTraveler.passportName,
                    passportNumber: editingTraveler.passportNumber,
                    ageType: editingTraveler.ageType
                }]
            };
            const res = await apiFetch(`/bookings/track/${tourData.confirmationNumber}/travelers`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
            if (res && res.success) {
                // Update local state
                const updatedDetails = [...tourData.travelers.details];
                updatedDetails[editingTraveler.index] = {
                    ...updatedDetails[editingTraveler.index],
                    nameOnPassport: editingTraveler.passportName,
                    passport: editingTraveler.passportNumber,
                    ageType: editingTraveler.ageType
                };
                setTourData({
                    ...tourData,
                    travelers: {
                        ...tourData.travelers,
                        details: updatedDetails
                    }
                });
                setEditingTraveler(null);
            } else {
                alert('Failed to update traveler');
            }
        } catch (error) {
            console.error(error);
            alert('Failed to update traveler');
        }
    };

    
    // Prevent body scroll when any modal is open (handles Lenis smooth scroll)
    useEffect(() => {
        const isAnyModalOpen = showGallery || showItinerary || activeProfilePhoto || showAccountDetails || showPaymentHistory || editingTraveler;
        
        if (isAnyModalOpen) {
            document.body.classList.add('overflow-hidden');
            document.documentElement.classList.add('overflow-hidden');
            if (window.__lenis) window.__lenis.stop();
        } else {
            document.body.classList.remove('overflow-hidden');
            document.documentElement.classList.remove('overflow-hidden');
            if (window.__lenis) window.__lenis.start();
        }
        
        return () => {
            document.body.classList.remove('overflow-hidden');
            document.documentElement.classList.remove('overflow-hidden');
            if (window.__lenis) window.__lenis.start();
        };
    }, [showGallery, showItinerary, activeProfilePhoto, showAccountDetails, showPaymentHistory, editingTraveler]);

    const nextPhoto = useCallback(() => {
        const photos = tourData?.vehiclePhotos || tourData?.vehicle?.images;
        if (photos && photos.length > 0) {
            setActivePhotoIndex(prev => (prev + 1) % photos.length);
        }
    }, [tourData]);

    const prevPhoto = useCallback(() => {
        const photos = tourData?.vehiclePhotos || tourData?.vehicle?.images;
        if (photos && photos.length > 0) {
            setActivePhotoIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1));
        }
    }, [tourData]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!tourId) return;
        setLoading(true);

        apiFetch(`/bookings/track/${tourId}`)
            .then(res => {
                if (res && res.success && res.data) {
                    setTourData(res.data);
                } else {
                    throw new Error('Invalid structure');
                }
            })
            .catch(() => {
                setTourData(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [tourId]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showGallery) return;
            if (e.key === 'ArrowRight') nextPhoto();
            if (e.key === 'ArrowLeft') prevPhoto();
            if (e.key === 'Escape') setShowGallery(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        
    

        
        const getInclusionIcon = (name) => {
        const n = name.toLowerCase();
        if (n.includes('hotel') || n.includes('accommodation') || n.includes('stay')) return 'fi-br-hotel';
        if (n.includes('breakfast') || n.includes('food') || n.includes('meal')) return 'fi-br-room-service';
        if (n.includes('transport') || n.includes('vehicle') || n.includes('car')) return 'fi-br-car-side';
        if (n.includes('guide') || n.includes('chauffeur')) return 'fi-br-user';
        if (n.includes('ticket') || n.includes('entrance') || n.includes('fee')) return 'fi-br-ticket';
        if (n.includes('visa')) return 'fi-br-document';
        if (n.includes('flight')) return 'fi-br-plane';
        return 'fi-br-check';
    };

    return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showGallery, nextPhoto, prevPhoto]);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEndHandler = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;
        
        if (isLeftSwipe) {
            nextPhoto();
        } else if (isRightSwipe) {
            prevPhoto();
        }
    };

    if (loading) {
        return (
            <div className='flex flex-col w-full min-h-screen justify-center items-center bg-gray-50 gap-6'>
                <div className='w-12 h-12 border-4 border-KITH-primary/30 border-t-KITH-primary rounded-full animate-spin'></div>
                <p className='text-gray-500 font-medium'>{t('my_tour.loading')}</p>
            </div>
        );
    }

    if (!tourData) {
        return (
            <div className='flex flex-col w-full min-h-screen justify-center items-center bg-gray-50 gap-6'>
                <div className='w-24 h-24 bg-red-100 text-red-500 rounded-full flex items-center justify-center text-4xl shadow-lg'>
                    <i className='fi fi-br-cross-circle'></i>
                </div>
                <div className='text-center flex flex-col gap-2'>
                    <h1 className='text-3xl font-black text-gray-800 uppercase tracking-tight'>{t('my_tour.not_found.title')}</h1>
                    <p className='text-gray-500 font-medium'>{t('my_tour.not_found.msg')} {tourId}</p>
                </div>
                <Link to="/find-my-tour" className='px-8 py-3 bg-KITH-primary text-white font-bold rounded-xl mt-4 hover:bg-KITH-primary-dark transition-all shadow-lg'>
                    {t('my_tour.not_found.back')}
                </Link>
            </div>
        );
    }

    // Map properties for UI display (live API only)
    const confirmationNumber = tourData.confirmationNumber;
    const tourCoordinator = tourData.coordinator ? tourData.coordinator.name : 'KITH Travels';
    const arrivalDate = tourData.arrival?.date ? new Date(tourData.arrival.date).toLocaleDateString() : '-';
    const departureDate = tourData.departure?.date ? new Date(tourData.departure.date).toLocaleDateString() : '-';
    const paxCount = tourData.travelers?.numberOfPax || 0;
    const itineraryFocus = tourData.tourPackage?.title || 'Custom Tour Plan';

    // Guide info mapping
    const guideName = tourData.guide ? `${tourData.guide.fname} ${tourData.guide.lname}` : 'Not Assigned';
    const guidePhoto = tourData.guide?.photo || null;

    // Chauffeur info mapping
    const chauffeurName = tourData.chauffeur ? `${tourData.chauffeur.fname} ${tourData.chauffeur.lname}` : 'Not Assigned';
    const chauffeurPhoto = tourData.chauffeur?.photo || null;

    // Vehicle info mapping
    const vehicleName = tourData.vehicle ? `${tourData.vehicle.brand} ${tourData.vehicle.model}` : 'Not Assigned';
    const vehiclePhotos = tourData.vehicle?.images || [];
    const licensePlate = tourData.vehicle?.licensePlate || 'Not Assigned';

    // Detailed Itinerary mapping
    const detailedItinerary = (tourData.itinerary || []).map(dayPlan => {
        const hotelNames = (dayPlan.hotels || []).map(h => h.name).join(', ');
        const experiencesList = (dayPlan.experiences || []).map(e => e.name || e).join(', ');
        let description = '';
        if (experiencesList) {
            description += `Experiences for today: ${experiencesList}. `;
        }
        if (hotelNames) {
            description += `Accommodation: staying at ${hotelNames}.`;
        }
        if (!description) {
            description = `Explore the beautiful areas of Sri Lanka.`;
        }
        return {
            day: dayPlan.day,
            location: dayPlan.location || 'Sri Lanka Destination',
            subtitle: dayPlan.subtitle || '',
            description: description,
            experiences: dayPlan.experiences || []
        };
    });

    // Travelers details mapping
    const travelersDetails = (tourData.travelers?.details || []).map(t => ({
        name: t.name,
        age: t.age,
        passportNumber: t.passportNumber || '-',
        passportName: t.nameOnPassport || '',
        ageType: t.ageType || 'adult'
    }));

    return (
        <div className='flex flex-col w-full min-h-screen bg-gray-50/50 pb-20'>
            <SEO title={t('my_tour.header.dashboard')} noindex={true} />
            {/* Minimal Header */}
            <div className='w-full bg-white px-[5%] md:px-[10%] py-6 flex justify-between items-center shadow-sm sticky top-0 z-50'>
                <Link to="/">
                    <img src={IMG.KITH_logo} alt="KITH Logo" className='w-24 md:w-32' />
                </Link>
                <div className='flex items-center gap-4'>
                    <span className='hidden md:block text-xs font-bold text-gray-400 uppercase tracking-widest'>{t('my_tour.header.dashboard')}</span>
                    <Link to="/find-my-tour" className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-KITH-primary hover:text-white transition-all'>
                        <i className='fi fi-br-cross text-xs'></i>
                    </Link>
                </div>
            </div>

            <div className='px-[5%] md:px-[10%] mt-12 w-full max-w-7xl mx-auto flex flex-col gap-10 animate-fadeIn'>

                {/* Header Card */}
                <div className='bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6'>
                    <div className='flex flex-col gap-2'>
                        <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>{t('my_tour.header.itinerary')}</span>
                        <h1 className='text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight text-left'>{t('my_tour.header.tour')} #{confirmationNumber}</h1>
                    </div>
                    <div className='flex items-center gap-4 bg-gray-50 px-6 py-4 rounded-2xl border border-gray-100'>
                        <div className='w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-KITH-primary'>
                            <i className='fi fi-br-headset text-xl'></i>
                        </div>
                        <div className='flex flex-col text-left'>
                            <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>{t('my_tour.header.coordinator')}</span>
                            <span className='font-black text-gray-800'>{tourCoordinator}</span>
                        </div>
                    </div>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-12 gap-10'>
                    {/* Left Col - Details */}
                    <div className='lg:col-span-7 flex flex-col gap-6'>
                        <div className='bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-6'>
                            <h2 className='text-lg font-black text-gray-800 uppercase tracking-tight text-left'>{t('my_tour.details.title')}</h2>

                            <div className='grid grid-cols-2 gap-6'>
                                <div className='flex flex-col gap-1 p-5 bg-gray-50 rounded-2xl border border-gray-100 text-left'>
                                    <span className='text-[10px] font-bold text-KITH-primary uppercase tracking-widest'>{t('my_tour.details.arrival')}</span>
                                    <span className='font-black text-gray-800'>{arrivalDate}</span>
                                </div>
                                <div className='flex flex-col gap-1 p-5 bg-gray-50 rounded-2xl border border-gray-100 text-left'>
                                    <span className='text-[10px] font-bold text-KITH-primary uppercase tracking-widest'>{t('my_tour.details.departure')}</span>
                                    <span className='font-black text-gray-800'>{departureDate}</span>
                                </div>
                            </div>
                            {/* Flight Details — clean read-only card */}
                             <div className='flex flex-col gap-3 p-5 bg-gray-50 rounded-2xl border border-gray-100 text-left'>
                                 <div className='flex justify-between items-center'>
                                     <span className='text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center gap-2'>
                                         <i className='fi fi-br-plane text-KITH-primary'></i>
                                         Flight Details
                                     </span>
                                 </div>
                                 <div className='grid grid-cols-2 gap-3'>
                                     {/* Arrival */}
                                     <div className='flex flex-col justify-center items-start gap-1.5 p-3 bg-white rounded-xl border border-gray-100'>
                                         <span className='text-[9px] font-black text-KITH-primary uppercase tracking-widest'>✈ Arrival Flight</span>
                                         {tourData.arrival?.flight ? <span className='font-black text-gray-800 text-lg bg-gray-50 px-3 py-1 rounded-lg border border-gray-100'>{tourData.arrival.flight}</span> : <span className='text-xs text-gray-400 italic'>Not provided</span>}
                                     </div>
                                     {/* Departure */}
                                     <div className='flex flex-col justify-center items-start gap-1.5 p-3 bg-white rounded-xl border border-gray-100'>
                                         <span className='text-[9px] font-black text-KITH-primary uppercase tracking-widest'>🛫 Departure Flight</span>
                                         {tourData.departure?.flight ? <span className='font-black text-gray-800 text-lg bg-gray-50 px-3 py-1 rounded-lg border border-gray-100'>{tourData.departure.flight}</span> : <span className='text-xs text-gray-400 italic'>Not provided</span>}
                                     </div>
                                 </div>
                             </div>

<div className='flex flex-col gap-4 mt-2'>
                                {[
                                    { id: 'tier', label: 'Tour Tier', value: tourData.tourTier === 'premium' ? 'Premium (Upgraded)' : 'Basic (Standard)', icon: 'fi-br-star' },
                                    { id: 'pax', label: t('my_tour.details.pax'), value: paxCount, icon: 'fi-br-users' },
                                    { id: 'itinerary', label: t('my_tour.details.focus'), value: itineraryFocus, icon: 'fi-br-map-marker' },
                                    { id: 'guide', label: t('my_tour.details.guide'), value: guideName, icon: 'fi-br-user', photo: guidePhoto },
                                    { id: 'chauffeur', label: t('my_tour.details.chauffeur'), value: chauffeurName, icon: 'fi-br-steering-wheel', photo: chauffeurPhoto },
                                    { id: 'vehicle', label: t('my_tour.details.vehicle'), value: vehicleName, icon: 'fi-br-car', photos: vehiclePhotos },
                                    { id: 'plate', label: t('my_tour.details.plate'), value: licensePlate, icon: 'fi-br-license-plate' }
                                ].map((item, idx) => (
                                    <div key={idx} className={`flex flex-col py-3 border-b border-gray-50 last:border-0 group`}>
                                        <div className='flex justify-between items-center w-full'>
                                            <div className='flex items-center gap-3'>
                                                <i className={`fi ${item.icon} text-gray-300 group-hover:text-KITH-primary transition-colors text-sm`}></i>
                                                <span className='text-xs font-bold text-gray-500 uppercase tracking-widest'>{item.label}</span>
                                            </div>
                                            <div className='flex items-center gap-3'>
                                                {item.id === 'itinerary' && (
                                                    <button onClick={() => setShowItinerary(true)} className='text-[10px] font-bold text-white bg-KITH-primary px-3 py-1 rounded-md hover:bg-KITH-primary-dark transition-all shadow-sm'>{t('my_tour.details.view_itinerary')}</button>
                                                )}
                                                {item.photo && <div className='w-[40px] h-[40px] min-w-[40px] flex-shrink-0 cursor-pointer rounded-full overflow-hidden shadow-sm' onClick={() => setActiveProfilePhoto(item.photo)}><DynamicImage image={item.photo} alt={item.label} className='w-full h-full' imgClassName='w-full h-full object-cover transition-transform duration-200 hover:scale-110' /></div>}
                                                {item.id !== 'itinerary' && (
                                                    <span 
                                                        className={`font-black text-sm ${item.photo ? 'cursor-pointer hover:text-KITH-primary transition-colors text-KITH-primary underline decoration-dotted underline-offset-4' : 'text-gray-800'}`}
                                                        onClick={() => item.photo && setActiveProfilePhoto(item.photo)}
                                                    >
                                                        {item.value}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {item.id === 'vehicle' && item.photos && item.photos.length > 0 && (
                                            <div className='flex gap-3 mt-3 ml-7'>
                                                {item.photos.map((photo, pIdx) => (
                                                    <div 
                                                        key={pIdx} 
                                                        className='w-[50px] h-[50px] min-w-[50px] aspect-square rounded-md overflow-hidden cursor-pointer hover:opacity-80 transition-all border border-gray-200 shadow-sm hover:scale-105 hover:border-KITH-primary'
                                                        onClick={() => { setActivePhotoIndex(pIdx); setShowGallery(true); }}
                                                    >
                                                        <DynamicImage
                                                            image={photo}
                                                            alt={`Vehicle ${pIdx + 1}`}
                                                            className='w-full h-full bg-gray-50'
                                                            imgClassName='w-full h-full object-cover'
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Col - Travelers & Payment Summary */}
                    <div className='lg:col-span-5 flex flex-col gap-8'>
                        {/* Live Payments Summary */}
                        {tourData.pricingSummary && (
                            <div className='bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-4 text-left cursor-pointer hover:border-KITH-primary/30 transition-all group' onClick={() => setShowPaymentHistory(true)}>
                                <div className='flex justify-between items-center'>
                                    <h2 className='text-sm font-black text-gray-800 uppercase tracking-tight'>{t('my_tour.payment.summary')}</h2>
                                    <i className='fi fi-br-angle-right text-gray-400 group-hover:text-KITH-primary transition-colors'></i>
                                </div>
                                {(!tourData.pricingSummary.totalPrice || tourData.pricingSummary.totalPrice === 0) ? (
                                    <div className='p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center gap-3'>
                                        <i className='fi fi-br-info text-amber-500 text-lg'></i>
                                        <div className='flex flex-col'>
                                            <span className='font-bold text-amber-700 text-sm'>{t('my_tour.payment.pending')}</span>
                                            <span className='text-[10px] text-amber-600 mt-0.5'>{t('my_tour.payment.pending_msg')}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='flex gap-4'>
                                        <div className='flex flex-col gap-1 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50 flex-1'>
                                            <span className='text-[9px] font-bold text-emerald-600 uppercase tracking-widest'>{t('my_tour.payment.paid')}</span>
                                            <span className='text-base font-black text-emerald-700'>${(tourData.pricingSummary.paidAmount || 0).toLocaleString()}</span>
                                        </div>
                                        <div className='flex flex-col gap-1 p-3 bg-amber-50/50 rounded-xl border border-amber-100/50 flex-1'>
                                            <span className='text-[9px] font-bold text-amber-600 uppercase tracking-widest'>{t('my_tour.payment.due')}</span>
                                            <span className={`text-base font-black ${tourData.pricingSummary.outstandingAmount > 0 ? 'text-amber-700 animate-pulse' : 'text-slate-500'}`}>
                                                ${(tourData.pricingSummary.outstandingAmount || 0).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); setShowAccountDetails(true); }} className='w-full py-3 bg-gray-50 text-gray-600 hover:text-KITH-primary text-xs font-bold uppercase tracking-widest rounded-xl border border-gray-200 transition-all'>
                                    {t('my_tour.payment.view_account')}
                                </button>
                            </div>
                        )}

                        <div className='bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-6 h-full text-left'>
                            <div className='flex justify-between items-center'>
                                <h2 className='text-lg font-black text-gray-800 uppercase tracking-tight'>{t('my_tour.travelers.title')}</h2>
                                <span className='bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black'>{travelersDetails.length} {t('my_tour.travelers.people')}</span>
                            </div>

                            <div className='flex flex-col gap-4 overflow-y-auto max-h-[400px] scrollbarThin pr-2'>
                                {travelersDetails.map((traveler, index) => (
                                    <div key={index} onClick={() => setEditingTraveler({ index, ...traveler })} className='bg-gray-50 p-5 rounded-2xl border border-gray-100 flex items-center gap-4 hover:shadow-md transition-all hover:border-KITH-primary/30 cursor-pointer'>
                                        <div className='w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center font-black text-gray-400 shrink-0'>
                                            {index + 1}
                                        </div>
                                        <div className='flex flex-col flex-1'>
                                            <span className='font-black text-gray-800 text-sm'>{traveler.name}</span>
                                            <div className='flex gap-4 mt-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest'>
                                                <span>{t('my_tour.travelers.type')}: <span className='text-KITH-primary'>{t(`my_tour.modals.update_pass.ages.${traveler.ageType || 'adult'}`)}</span></span>
                                                <span>{t('my_tour.travelers.pass')}: <span className='text-gray-600'>{traveler.passportNumber === '-' || !traveler.passportNumber ? t('my_tour.travelers.missing') : traveler.passportNumber}</span></span>
                                            </div>
                                            {(!traveler.passportNumber || traveler.passportNumber === '-') && (
                                                <div className='mt-2 flex items-center gap-2 p-2 bg-amber-50 text-amber-700 text-[10px] rounded-lg font-semibold uppercase tracking-wide border border-amber-100'>
                                                    <i className='fi fi-br-exclamation text-amber-500'></i> 
                                                    <span>{t('my_tour.travelers.missing_alert')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Photo Modal */}
            {activeProfilePhoto && (
                <div className='fixed inset-0 z-[100] bg-black/80 flex items-center justify-center animate-fadeIn' onClick={() => setActiveProfilePhoto(null)}>
                    <button onClick={() => setActiveProfilePhoto(null)} aria-label="Close profile photo modal" className='absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50'>
                        <i className='fi fi-br-cross'></i>
                    </button>
                    <DynamicImage image={activeProfilePhoto} alt="Profile" className='w-64 h-64 md:w-96 md:h-96 overflow-hidden rounded-full shadow-2xl animate-fadeIn transition-all border-4 border-white' imgClassName='w-full h-full object-cover' onClick={(e) => e.stopPropagation()} />
                </div>
            )}

            {/* Flight Details Edit Modal */}
            {editingFlights && (
                <div className='fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn' onClick={(e) => { if (e.target === e.currentTarget) setEditingFlights(false); }}>
                    <div className='bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 flex flex-col gap-6 relative'>
                        <div className='flex justify-between items-center'>
                            <div className='flex flex-col gap-1 text-left'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>Flight Information</span>
                                <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight'>Edit Flight Details</h2>
                            </div>
                            <button onClick={() => setEditingFlights(false)} className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-KITH-primary hover:text-white transition-all shrink-0'>
                                <i className='fi fi-br-cross text-xs'></i>
                            </button>
                        </div>
                        <div className='flex flex-col gap-5 text-left'>
                            {/* Arrival */}
                            <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>✈ Arrival</span>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>Date & Time</label>
                                    <div className='p-3 bg-gray-100 rounded-xl border-2 border-gray-100 text-gray-500 font-bold text-sm'>
                                        {tourData.arrival?.date || 'Date TBA'} {tourData.arrival?.time ? `at ${tourData.arrival.time}` : ''}
                                    </div>
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>Flight No.</label>
                                    <input type='text' placeholder='e.g. UL 315' value={flightForm.arrival.flight} onChange={e => setFlightForm({...flightForm, arrival: {...flightForm.arrival, flight: e.target.value}})} className='p-3 bg-white rounded-xl border-2 border-gray-100 focus:border-KITH-primary outline-none transition-all font-bold text-sm uppercase placeholder:font-normal placeholder:normal-case' />
                                </div>
                            </div>
                            {/* Departure */}
                            <div className='flex flex-col gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>🛫 Departure</span>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>Date & Time</label>
                                    <div className='p-3 bg-gray-100 rounded-xl border-2 border-gray-100 text-gray-500 font-bold text-sm'>
                                        {tourData.departure?.date || 'Date TBA'} {tourData.departure?.time ? `at ${tourData.departure.time}` : ''}
                                    </div>
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>Flight No.</label>
                                    <input type='text' placeholder='e.g. UL 316' value={flightForm.departure.flight} onChange={e => setFlightForm({...flightForm, departure: {...flightForm.departure, flight: e.target.value}})} className='p-3 bg-white rounded-xl border-2 border-gray-100 focus:border-KITH-primary outline-none transition-all font-bold text-sm uppercase placeholder:font-normal placeholder:normal-case' />
                                </div>
                            </div>
                            <div className='flex gap-3'>
                                <button onClick={() => setEditingFlights(false)} className='flex-1 py-3 bg-gray-100 text-gray-700 font-black rounded-xl hover:bg-gray-200 transition-all'>Cancel</button>
                                <button onClick={async () => { await handleFlightSave(); setEditingFlights(false); }} className='flex-[2] py-3 bg-KITH-primary text-white font-black rounded-xl hover:bg-KITH-primary-dark transition-all shadow-lg active:scale-95'>Save Flight Details</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Vehicle Gallery Modal */}
            {showGallery && (vehiclePhotos.length > 0) && (
                <div 
                    className='fixed inset-0 z-[100] bg-black/90 flex items-center justify-center animate-fadeIn'
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEndHandler}
                >
                    <button onClick={() => setShowGallery(false)} aria-label="Close vehicle gallery modal" className='absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50'>
                        <i className='fi fi-br-cross'></i>
                    </button>

                    <button onClick={prevPhoto} aria-label="Previous vehicle photo" className='absolute left-4 md:left-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50'>
                        <i className='fi fi-br-angle-left'></i>
                    </button>

                    <img src={vehiclePhotos[activePhotoIndex]?.h || vehiclePhotos[activePhotoIndex]?.l || vehiclePhotos[activePhotoIndex]} alt="Vehicle" className='max-w-[85vw] max-h-[80vh] w-auto h-auto object-contain rounded-xl shadow-2xl animate-fadeIn transition-all' />

                    <button onClick={nextPhoto} aria-label="Next vehicle photo" className='absolute right-4 md:right-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all z-50'>
                        <i className='fi fi-br-angle-right'></i>
                    </button>

                    <div className='absolute bottom-6 flex gap-3 z-50'>
                        {vehiclePhotos.map((_, idx) => (
                            <div key={idx} onClick={() => setActivePhotoIndex(idx)} className={`w-3 h-3 rounded-full cursor-pointer transition-all ${idx === activePhotoIndex ? 'bg-KITH-primary scale-125' : 'bg-white/30 hover:bg-white/50'}`}></div>
                        ))}
                    </div>
                </div>
            )}

            {/* Itinerary Modal */}
            {showItinerary && detailedItinerary && detailedItinerary.length > 0 && (
                <div className='fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn'>
                    <div className='bg-white w-full max-w-3xl max-h-[85vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col'>
                        <div className='p-6 md:p-8 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-20 shadow-sm'>
                            <div className='flex items-center gap-4 text-left'>
                                <img src={IMG.KITH_logo} alt="KITH Logo" className='w-16 md:w-20 hidden sm:block' />
                                <div className='flex flex-col'>
                                    <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>KITH Travels - Official Itinerary</span>
                                    <h2 className='text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight'>Detailed Itinerary</h2>
                                </div>
                            </div>
                            <div className='flex items-center gap-4'>
                                <button 
                                    onClick={handleDownloadPDF}
                                    disabled={isGeneratingPDF}
                                    className={`hidden md:flex px-4 py-2 ${isGeneratingPDF ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800 active:scale-95 hover:shadow-md'} text-white font-medium text-xs rounded-full transition-all duration-300 shadow-sm items-center gap-2`}
                                >
                                    {isGeneratingPDF ? <i className="fi fi-br-spinner animate-spin"></i> : <i className="fi fi-br-download"></i>}
                                    {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
                                </button>
                                <button onClick={() => setShowItinerary(false)} aria-label="Close detailed itinerary modal" className='w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-all shrink-0'>
                                    <i className='fi fi-br-cross text-xs'></i>
                                </button>
                            </div>
                        </div>
                        
                        <div className='p-6 bg-gray-50 border-b border-gray-200 flex flex-col gap-4 text-left'>
                            <div className='flex justify-between items-center mb-2 md:hidden'>
                                <button 
                                    onClick={handleDownloadPDF}
                                    disabled={isGeneratingPDF}
                                    className={`w-full justify-center px-4 py-3 ${isGeneratingPDF ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800 active:scale-95 hover:shadow-md'} text-white font-medium text-xs rounded-xl transition-all duration-300 shadow-sm flex items-center gap-2`}
                                >
                                    {isGeneratingPDF ? <i className="fi fi-br-spinner animate-spin text-sm"></i> : <i className="fi fi-br-download text-sm"></i>}
                                    {isGeneratingPDF ? 'Generating PDF...' : 'Download Itinerary (PDF)'}
                                </button>
                            </div>
                            <div className='flex justify-between items-center'>
                                <span className='text-sm font-black text-gray-800'>Client: {tourData.customer?.fname} {tourData.customer?.lname}</span>
                                <span className='text-xs font-bold text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200'>{arrivalDate} - {departureDate}</span>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-2'>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2'><i className='fi fi-br-plane-arrival text-KITH-primary'></i> Arrival Details</label>
                                    <div className='w-full bg-white px-4 py-3 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 shadow-sm'>{tourData.arrival?.flight || 'Not specified'} - {tourData.arrival?.time || 'Time TBA'}</div>
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2'><i className='fi fi-br-plane-departure text-KITH-primary'></i> Departure Details</label>
                                    <div className='w-full bg-white px-4 py-3 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 shadow-sm'>{tourData.departure?.flight || 'Not specified'} - {tourData.departure?.time || 'Time TBA'}</div>
                                </div>
                            </div>
                            
                            {/* Featured Inclusions Row */}
                            {(tourData.featuredInclusions?.length > 0 || tourData.includes?.length > 0) && (
                                <div className='mt-4 pt-4 border-t border-gray-100 flex flex-col gap-3'>
                                    <span className='text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2'><i className='fi fi-br-star text-KITH-primary'></i> Featured Inclusions</span>
                                    <div className='flex flex-wrap gap-2'>
                                        {(tourData.featuredInclusions || tourData.includes || []).map((inc, i) => (
                                            <span key={i} className='px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-full shadow-sm flex items-center gap-2'>
                                                <i className={`fi ${getInclusionIcon(inc)} text-emerald-500 text-[10px]`}></i> {inc}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className='p-6 md:p-10 overflow-y-auto flex flex-col gap-8 scrollbarThin text-left bg-gray-100'>
                            {detailedItinerary.map((dayPlan, idx) => (
                                <div key={idx} className='bg-white rounded-3xl border border-gray-200 p-6 md:p-8 shadow-sm relative flex flex-col gap-5 hover:shadow-md transition-shadow'>
                                    <div className='flex items-center gap-5 border-b border-gray-100 pb-5'>
                                        <div className='bg-gray-900 text-white font-black px-4 py-3 rounded-xl flex flex-col items-center justify-center shrink-0 min-w-[70px] shadow-sm'>
                                            <span className='text-[10px] uppercase tracking-widest opacity-80'>Day</span>
                                            <span className='text-xl leading-none mt-1'>{String(dayPlan.day).padStart(2, '0')}</span>
                                        </div>
                                        <div className='flex flex-col'>
                                            <h3 className='text-2xl font-serif font-bold text-gray-800 leading-tight'>{dayPlan.location}</h3>
                                            <span className='text-sm text-KITH-primary font-bold mt-1'>{dayPlan.subtitle || 'Morning till Evening'}</span>
                                        </div>
                                    </div>
                                    <p className='text-gray-600 text-sm md:text-base leading-relaxed'>
                                        {dayPlan.description}
                                    </p>
                                    
                                    {dayPlan.experiences && dayPlan.experiences.length > 0 && (
                                        <div className='mt-5 pt-5 border-t border-gray-100'>
                                            <h4 className='text-[10px] font-bold text-KITH-primary uppercase tracking-widest mb-4 flex items-center gap-2'><i className='fi fi-br-map-marker text-gray-400'></i> Included Experiences</h4>
                                            <div className='flex flex-col gap-4'>
                                                {dayPlan.experiences.map((exp, i) => (
                                                    <div key={i} className='flex gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                                                        <div className='w-24 h-24 sm:w-32 sm:h-32 shrink-0 rounded-xl overflow-hidden shadow-sm'>
                                                            <DynamicImage image={exp.image} alt={exp.name || exp} className='w-full h-full bg-gray-200' imgClassName='w-full h-full object-cover' />
                                                        </div>
                                                        <div className='flex flex-col justify-center gap-1.5'>
                                                            <h5 className='font-black text-gray-800 text-sm md:text-base'>{exp.name || exp}</h5>
                                                            {exp.description && <p className='text-xs md:text-sm text-gray-500 leading-relaxed line-clamp-3'>{exp.description}</p>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {/* Advisory placed at end of itinerary modal (not footer) */}
                            <div className='bg-white rounded-2xl border border-gray-200 p-4 md:p-6 text-sm text-gray-600 shadow-sm'>
                                <p className='mb-2 text-red-500 font-bold'>We strongly recommend arriving at the airport a minimum of 3 hours prior to your scheduled departure time to avoid any unforeseen delays or complications at immigration.</p>
                                <p className='text-gray-500'>This is a system-generated itinerary. Please contact your dedicated KITH Travels tour coordinator for any clarifications or amendments before travel.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

                                                {/* Account Details Modal */}
            {showAccountDetails && (
                <div 
                    className='fixed inset-0 z-[100] bg-black/60 backdrop-blur-md animate-fadeIn flex items-center justify-center p-4 sm:p-6'
                    onClick={(e) => { if(e.target === e.currentTarget) setShowAccountDetails(false); }}
                >
                    <div className='bg-white w-full max-w-xl rounded-[2rem] shadow-2xl flex flex-col relative overflow-hidden' style={{ maxHeight: '90vh' }}>
                        {/* Fixed Header */}
                        <div className='flex justify-between items-center p-6 sm:p-8 pb-4 border-b border-gray-100 shrink-0'>
                            <h2 className='text-xl sm:text-2xl font-black text-gray-800 uppercase tracking-tight'>Bank Transfer Details</h2>
                            <button onClick={() => setShowAccountDetails(false)} className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-KITH-primary hover:text-white transition-all'>
                                <i className='fi fi-br-cross text-xs'></i>
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className='flex-1 p-6 sm:p-8 pt-4 flex flex-col gap-4 text-left overflow-y-auto custom-scrollbar min-h-0' data-lenis-prevent="true">
                            <div className='flex flex-col gap-2 p-5 bg-emerald-50 rounded-2xl border border-emerald-100 shrink-0'>
                                <span className='text-xs font-bold text-emerald-600 uppercase tracking-widest'>Important Note</span>
                                <span className='text-sm text-emerald-800'>Where payment is made by bank transfer, please use the payment reference instructed and ensure sufficient information is provided to identify the booking. Any intermediary bank charges or exchange losses are the traveler's responsibility.</span>
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 shrink-0'>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Beneficiary Name</span>
                                    <span className='font-black text-gray-800 text-sm'>KITH Travels (Pvt) Ltd</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Account Number</span>
                                    <span className='font-black text-gray-800 text-sm'>96845948</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Bank</span>
                                    <span className='font-black text-gray-800 text-sm'>Bank of Ceylon (BOC)</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Branch</span>
                                    <span className='font-black text-gray-800 text-sm'>Dodangoda</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Bank Code</span>
                                    <span className='font-black text-gray-800 text-sm'>7010</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Branch Code</span>
                                    <span className='font-black text-gray-800 text-sm'>293</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>SWIFT Code</span>
                                    <span className='font-black text-gray-800 text-sm'>BCEYLKLX</span>
                                </div>
                                <div className='flex flex-col gap-1 p-4 bg-gray-50 rounded-xl border border-gray-100'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Currency</span>
                                    <span className='font-black text-gray-800 text-sm'>USD</span>
                                </div>
                            </div>
                            
                            <div className='flex flex-col gap-2 p-4 bg-gray-50 rounded-xl border border-gray-100 shrink-0 mt-2'>
                                <div className='flex flex-col gap-1'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Payment Reference</span>
                                    <span className='font-black text-gray-800 text-sm'>[Booking ID / Client Name]</span>
                                </div>
                                <div className='flex flex-col gap-1 mt-2'>
                                    <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>Send Payment Proof To</span>
                                    <span className='font-black text-KITH-primary text-sm'>booking@kithtravels.com</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment History Modal */}
            {showPaymentHistory && (
                <div className='fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn'>
                    <div className='bg-white w-full max-w-lg rounded-[2rem] shadow-2xl p-8 flex flex-col gap-6 relative'>
                        <div className='flex justify-between items-center mb-6'>
                            <h2 className='text-2xl font-black text-gray-800 uppercase tracking-tight'>{t('my_tour.payment.history')}</h2>
                            <button onClick={() => setShowPaymentHistory(false)} className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-KITH-primary hover:text-white transition-all'>
                                <i className='fi fi-br-cross text-xs'></i>
                            </button>
                        </div>
                        <div className='flex flex-col gap-3 mt-4 overflow-y-auto max-h-[50vh] scrollbarThin pr-2'>
                            {tourData.payments && tourData.payments.length > 0 ? (
                                tourData.payments.map((pay, i) => (
                                    <div key={i} className='flex justify-between items-center p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                                        <div className='flex flex-col gap-1'>
                                            <span className='text-xs font-black text-gray-800'>{pay.date}</span>
                                            <span className='text-[10px] font-bold text-gray-500 uppercase tracking-widest'>{pay.method}</span>
                                        </div>
                                        <span className='font-black text-emerald-600'>+ ${pay.amount.toLocaleString()}</span>
                                    </div>
                                ))
                            ) : (
                                <div className='text-center py-10 flex flex-col items-center gap-4'>
                                    <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400'>
                                        <i className='fi fi-br-file-invoice text-2xl'></i>
                                    </div>
                                    <p className='text-gray-500 font-bold'>{t('my_tour.payment.no_payments')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Editing Traveler Modal */}
            {editingTraveler && (
                <div className='fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn'>
                    <div className='bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 flex flex-col gap-6 relative'>
                        <div className='flex justify-between items-center mb-6'>
                            <div className='flex flex-col gap-1 text-left'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>{t('my_tour.modals.update_pass.title1')}</span>
                                <h2 className='text-2xl font-black text-gray-800 uppercase tracking-tight'>{t('my_tour.modals.update_pass.title2')}</h2>
                            </div>
                            <button onClick={() => setEditingTraveler(null)} className='w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center hover:bg-KITH-primary hover:text-white transition-all shrink-0'>
                                <i className='fi fi-br-cross text-xs'></i>
                            </button>
                        </div>

                        <div className='flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100 mb-6 text-left'>
                            <i className='fi fi-br-shield-check text-amber-500 mt-0.5'></i>
                            <p className='text-xs font-bold text-amber-700 leading-relaxed'>
                                {t('my_tour.modals.update_pass.alert')}
                            </p>
                        </div>

                        <div className='flex flex-col gap-5 text-left'>
                            <div className='flex flex-col gap-2'>
                                <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-2'>{t('my_tour.modals.update_pass.name')}</label>
                                <input type="text" value={editingTraveler.name || ''} readOnly className='p-4 bg-gray-50 rounded-xl border border-gray-100 text-gray-500 font-bold' />
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-2'>{t('my_tour.modals.update_pass.name_pass')}</label>
                                    <input 
                                        type="text" 
                                        value={editingTraveler.passportName || ''} 
                                        onChange={(e) => setEditingTraveler({...editingTraveler, passportName: e.target.value})}
                                        className='p-4 bg-white rounded-xl border-2 border-gray-100 focus:border-KITH-primary outline-none transition-all font-bold placeholder:font-normal'
                                        placeholder="JOHN DOE"
                                    />
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-2'>{t('my_tour.modals.update_pass.pass_num')}</label>
                                    <input 
                                        type="text" 
                                        value={editingTraveler.passportNumber || ''} 
                                        onChange={(e) => setEditingTraveler({...editingTraveler, passportNumber: e.target.value})}
                                        className='p-4 bg-white rounded-xl border-2 border-gray-100 focus:border-KITH-primary outline-none transition-all font-bold placeholder:font-normal uppercase'
                                        placeholder="N12345678"
                                    />
                                </div>
                            </div>

                            <div className='flex flex-col gap-2'>
                                <label className='text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-2'>{t('my_tour.modals.update_pass.age')}</label>
                                <select 
                                    value={editingTraveler.ageType || 'adult'} 
                                    onChange={(e) => setEditingTraveler({...editingTraveler, ageType: e.target.value})}
                                    className='p-4 bg-white rounded-xl border-2 border-gray-100 focus:border-KITH-primary outline-none transition-all font-bold appearance-none cursor-pointer'
                                >
                                    <option value="adult">{t('my_tour.modals.update_pass.ages.adult')}</option>
                                    <option value="kid">{t('my_tour.modals.update_pass.ages.kid')}</option>
                                    <option value="infant">{t('my_tour.modals.update_pass.ages.infant')}</option>
                                    <option value="senior">{t('my_tour.modals.update_pass.ages.senior')}</option>
                                </select>
                            </div>

                            <button onClick={handleTravelerSave} className='mt-4 w-full py-4 bg-KITH-primary text-white font-black rounded-xl hover:bg-KITH-primary-dark transition-all shadow-lg active:scale-95'>
                                {t('my_tour.modals.update_pass.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MyTour